import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Text } from '@change/ds-core-ui';
import { Form } from '@change/ds-forms';
import { InfoV2Icon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate } from '@change-corgi/core/react/i18n';

import photoPlaceholder from 'src/assets/starterDashboardV2/endorsements/photo-placeholder.png';

import { EndorsementCardEdit } from 'src/app/shared/components/endorsements';

import { useEndorsementFocus } from '../../hooks/useEndorsementFocus';
import type { EndorsementFormData } from '../../shared/types';
import { endorsementFormSchema, type EndorsementFormSchema } from '../../shared/validation';
import { EndorsementModal } from '../EndorsementModal';

import { EndorsementFormFields } from './Fields';
import { EndorsementFormFooter } from './Footer';
import { EndorsementFormHeader } from './Header';

type PreviewSectionProps = {
	formData: EndorsementFormSchema;
	image: string | undefined;
	focusedField: ReturnType<typeof useEndorsementFocus>['focusedField'];
	previewSection: string;
	onHelpClick: () => void;
};

const PreviewSection: React.FC<PreviewSectionProps> = ({
	formData,
	image,
	focusedField,
	previewSection,
	onHelpClick,
}) => (
	<div className={previewSection}>
		<Text variant="body" fontWeight="bold" className="text-typography-strong mb-4">
			<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.preview_heading" />
		</Text>
		<EndorsementCardEdit
			name={formData.name}
			title={formData.title}
			description={formData.statement}
			photoUrl={image || formData.photoUrl || photoPlaceholder}
			link={formData.url}
			focusedField={focusedField}
			displaySeparator
		/>

		<button
			type="button"
			className="mt-6 flex cursor-pointer items-start gap-2 rounded-2xl p-0"
			onClick={onHelpClick}
			data-testid="endorsement-help-link"
			data-qa="endorsement-help-link"
		>
			<InfoV2Icon className="text-typography-strong" size="medium" />
			<Text variant="body" fontWeight="bold" className="text-typography-strong underline">
				<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.view_endorsement_example" />
			</Text>
		</button>
	</div>
);

type ModalsProps = {
	isHelpModalOpen: boolean;
	isSubmitModalOpen: boolean;
	isSubmitting: boolean;
	onCloseHelpModal: () => void;
	onCloseSubmitModal: () => void;
	onConfirmSubmit: () => void | Promise<void>;
	formData: EndorsementFormSchema;
	isTermsAgreed: boolean;
	onTermsChange: (checked: boolean) => void;
};

const Modals: React.FC<ModalsProps> = ({
	isHelpModalOpen,
	isSubmitModalOpen,
	isSubmitting,
	onCloseHelpModal,
	onCloseSubmitModal,
	onConfirmSubmit,
	formData,
	isTermsAgreed,
	onTermsChange,
}) => (
	<>
		<EndorsementModal
			variant="help"
			isOpen={isHelpModalOpen}
			onClose={onCloseHelpModal}
			formData={{ ...formData, statement: formData.statement || '' }}
		/>
		<EndorsementModal
			variant="submit"
			isOpen={isSubmitModalOpen}
			isSubmitting={isSubmitting}
			onClose={onCloseSubmitModal}
			onConfirm={onConfirmSubmit}
			formData={{ ...formData, statement: formData.statement || '' }}
			isTermsAgreed={isTermsAgreed}
			onTermsChange={onTermsChange}
		/>
	</>
);

type EndorsementFormProps = {
	title: string;
	backButtonAriaLabel: string;
	defaultValues: EndorsementFormData;
	isSubmitting: boolean;
	isHelpModalOpen: boolean;
	isSubmitModalOpen: boolean;
	onBack: () => void;
	onSubmit: (data: EndorsementFormSchema) => void | Promise<void>;
	onInvalidSubmit?: () => void;
	onHelpClick: () => void;
	onCloseHelpModal: () => void;
	onCloseSubmitModal: () => void;
	onConfirmSubmit: () => void | Promise<void>;
	pageContainer: string;
	contentContainer: string;
	formSection: string;
	previewSection: string;
	testId?: string;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
	uploadErrorMessage?: TranslationKey | null;
	isTermsAgreed: boolean;
	onTermsChange: (checked: boolean) => void;
};

export const EndorsementForm: React.FC<EndorsementFormProps> = ({
	title,
	backButtonAriaLabel,
	defaultValues,
	isSubmitting,
	isHelpModalOpen,
	isSubmitModalOpen,
	onBack,
	onSubmit,
	onInvalidSubmit,
	onHelpClick,
	onCloseHelpModal,
	onCloseSubmitModal,
	onConfirmSubmit,
	pageContainer,
	contentContainer,
	formSection,
	previewSection,
	testId,
	uppy,
	image,
	uploadErrorMessage,
	isTermsAgreed,
	onTermsChange,
}) => {
	const { focusedField, handleFieldFocus, handleFieldBlur } = useEndorsementFocus();

	return (
		<Form
			schema={endorsementFormSchema}
			defaultValues={defaultValues}
			onSubmit={onSubmit}
			className={pageContainer}
			data-testid={testId}
			data-qa="endorsement-form"
		>
			{({ watch, handleSubmit, setValue }) => {
				const formData = watch();
				const statementCharCount = (formData.statement || '').length;

				const handleFormSubmit = async () => {
					// Sync photoUrl field with image state before validation
					setValue('photoUrl', image || '', { shouldValidate: false });

					await handleSubmit(onSubmit, () => {
						// Called when validation fails
						onInvalidSubmit?.();
					})();
				};

				return (
					<>
						<EndorsementFormHeader onBack={onBack} title={title} backButtonAriaLabel={backButtonAriaLabel} />
						<main className={contentContainer}>
							<div className={formSection}>
								<EndorsementFormFields
									statementCharCount={statementCharCount}
									uppy={uppy}
									image={image}
									onFieldFocus={handleFieldFocus}
									onFieldBlur={handleFieldBlur}
									uploadErrorMessage={uploadErrorMessage}
								/>
							</div>
							<PreviewSection
								formData={formData}
								image={image}
								focusedField={focusedField}
								previewSection={previewSection}
								onHelpClick={onHelpClick}
							/>
						</main>
						<EndorsementFormFooter onClick={handleFormSubmit} data-qa="endorsement-form-footer" />
						<Modals
							isHelpModalOpen={isHelpModalOpen}
							isSubmitModalOpen={isSubmitModalOpen}
							isSubmitting={isSubmitting}
							onCloseHelpModal={onCloseHelpModal}
							onCloseSubmitModal={onCloseSubmitModal}
							onConfirmSubmit={onConfirmSubmit}
							formData={formData}
							isTermsAgreed={isTermsAgreed}
							onTermsChange={onTermsChange}
						/>
					</>
				);
			}}
		</Form>
	);
};
